import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Send, Square, Plus, History, PanelRightClose, Columns2, Maximize2, Minimize2, Sparkles, AlertTriangle, Boxes, Users, Truck, FolderKanban, CalendarRange, ListChecks, Wrench, Building2, Inbox, MapPin, Warehouse, TrendingUp, FileText, Clock, X, BedDouble, UtensilsCrossed, Receipt, BellRing } from "lucide-react";
import { cn } from "@/lib/utils";
import { get } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAssistant } from "@/context/AssistantContext";
import { AssistantMessage, UserBubble } from "./Message";
import { Sessions } from "./Sessions";

const ICONS = { AlertTriangle, Boxes, Users, Truck, History, FolderKanban, CalendarRange, ListChecks, Wrench, Building2, Inbox, MapPin, Warehouse, TrendingUp, FileText, Clock, BedDouble, UtensilsCrossed, Receipt, BellRing };

const IconBtn = ({ icon: Icon, label, onClick, testId, active, className }) => (
  <button type="button" onClick={onClick} title={label} aria-label={label} className={cn("inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:outline-none", active && "bg-muted text-foreground", className)} data-testid={testId}>
    <Icon className="h-4 w-4" strokeWidth={1.75} />
  </button>
);

const contextLabel = (ctx) => {
  if (!ctx) return null;
  if (ctx.project_id) return "Dette prosjektet";
  if (ctx.module_id) return "Denne modulen";
  if (ctx.customer_id) return "Denne kunden";
  if (ctx.lead_id) return "Denne henvendelsen";
  if (ctx.quote_id) return "Dette tilbudet";
  if (ctx.location_id) return "Valgt sted på kartet";
  const p = ctx.path || "";
  const names = { "/": "Oversikt", "/kart": "Kart", "/planlegging": "Planlegging", "/moduler": "Flåte", "/service": "Service", "/henvendelser": "Henvendelser", "/tilbud": "Tilbud", "/prosjekter": "Prosjekter", "/kunder": "Kunder", "/assistent": null };
  for (const k of Object.keys(names)) if (p === k || (k !== "/" && p.startsWith(k))) return names[k];
  return null;
};

/** Empty state: context-aware starters. */
const Starters = ({ ctx, onPick, dense }) => {
  const q = useQuery({ queryKey: ["ai-suggestions", ctx?.path, ctx?.project_id, ctx?.module_id, ctx?.customer_id], queryFn: () => get("/ai/suggestions", { path: ctx?.path, project_id: ctx?.project_id, module_id: ctx?.module_id, customer_id: ctx?.customer_id }), staleTime: 60_000 });
  const label = contextLabel(ctx);
  return (
    <div className={cn("mx-auto w-full", dense ? "max-w-full" : "max-w-2xl")} data-testid="ai-starters">
      <div className={cn("flex flex-col items-start gap-3", !dense && "pt-6")}>
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary" aria-hidden>
          <Sparkles className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div>
          <h2 className={cn("font-semibold tracking-tight", dense ? "text-[16px] leading-6" : "text-[22px] leading-7")}>Hva vil du vite – eller få gjort?</h2>
          <p className="mt-1 text-[12.5px] leading-5 text-muted-foreground">
            Jeg svarer kun fra faktiske data i systemet og viser grunnlaget under hvert svar. Endringer legges klare som utkast du bekrefter.
            {label && (
              <>
                {" "}
                Kontekst nå: <span className="font-medium text-foreground/80">{label}</span>.
              </>
            )}
          </p>
        </div>
      </div>
      <div className={cn("mt-5 grid gap-2", dense ? "grid-cols-1" : "sm:grid-cols-2")}>
        {(q.data || []).map((s, i) => {
          const Icon = ICONS[s.icon] || Sparkles;
          return (
            <button key={i} type="button" onClick={() => onPick(s.text)} className="group flex items-start gap-3 rounded-xl bg-card px-3.5 py-3 text-left ring-1 ring-black/[0.05] transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-pop dark:ring-white/10" data-testid="ai-suggestion">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" strokeWidth={1.75} />
              <span className="text-[13px] leading-5">{s.text}</span>
            </button>
          );
        })}
        {q.isLoading && [0, 1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-muted" />)}
      </div>
    </div>
  );
};

/**
 * The conversation surface shared by dock, split and full-screen. `variant` only changes chrome and density.
 */
export const Conversation = ({ variant = "dock", className }) => {
  const navigate = useNavigate();
  const { mode, setMode, close, messages, streaming, progress, ask, stop, newSession, pageContext, draft, setDraft } = useAssistant();
  const [histOpen, setHistOpen] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const dense = variant !== "full";
  const ctxLabel = contextLabel(pageContext);

  // stick to bottom while streaming
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: streaming ? "auto" : "smooth" });
  }, [messages, progress, streaming]);

  useEffect(() => {
    if (mode !== "closed") setTimeout(() => inputRef.current?.focus(), 60);
  }, [mode]);

  const send = (text) => {
    const t = (text ?? draft).trim();
    if (!t) return;
    setDraft("");
    ask(t);
  };
  const lastAssistant = useMemo(() => [...messages].reverse().find((m) => m.role === "assistant"), [messages]);

  return (
    <div className={cn("flex h-full min-h-0 flex-col bg-background", className)} data-testid="ai-assistant-panel" data-variant={variant}>
      {/* header */}
      <header className="flex h-12 shrink-0 items-center gap-1 border-b border-border/70 px-3 dark:border-white/5">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary" aria-hidden>
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
        </span>
        <span className="ml-1 text-[13px] font-semibold">Assistent</span>
        {ctxLabel && (
          <span className="ml-2 hidden max-w-[180px] truncate rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground sm:inline" data-testid="ai-context-chip" title={pageContext?.path}>
            {ctxLabel}
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          <Popover open={histOpen} onOpenChange={setHistOpen}>
            <PopoverTrigger asChild>
              <button type="button" title="Historikk" aria-label="Historikk" className={cn("inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground", histOpen && "bg-muted text-foreground")} data-testid="ai-history-button">
                <History className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[320px] p-0">
              <Sessions className="max-h-[420px]" onPick={() => setHistOpen(false)} />
            </PopoverContent>
          </Popover>
          <IconBtn icon={Plus} label="Ny samtale" onClick={newSession} testId="ai-assistant-reset-button" />
          <span className="mx-1 h-4 w-px bg-border" aria-hidden />
          {variant !== "split" && <IconBtn icon={Columns2} label="Utvid til delt visning" onClick={() => setMode("split")} testId="ai-mode-split" />}
          {variant === "split" && <IconBtn icon={PanelRightClose} label="Til dokk" onClick={() => setMode("dock")} testId="ai-mode-dock" />}
          {variant !== "full" && (
            <IconBtn
              icon={Maximize2}
              label="Fullskjerm"
              onClick={() => {
                setMode("closed");
                navigate("/assistent");
              }}
              testId="ai-mode-full"
            />
          )}
          {variant === "full" && <IconBtn icon={Minimize2} label="Tilbake til dokk" onClick={() => { navigate(-1); setMode("dock"); }} testId="ai-mode-exit-full" />}
          {variant !== "full" && <IconBtn icon={X} label="Lukk (Esc)" onClick={close} testId="ai-assistant-close" />}
        </div>
      </header>

      {/* messages */}
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto scrollbar-thin" data-testid="ai-messages">
        <div className={cn("mx-auto flex w-full flex-col gap-5 px-4 py-4", variant === "full" ? "max-w-3xl px-6 py-8" : "")}>
          {messages.length === 0 && <Starters ctx={pageContext} onPick={send} dense={dense} />}
          {messages.map((m) => (m.role === "user" ? <UserBubble key={m.id} m={m} /> : <AssistantMessage key={m.id} m={m} progress={m.id === lastAssistant?.id ? progress : null} dense={dense} onFollowup={send} />))}
        </div>
      </div>

      {/* composer */}
      <div className={cn("shrink-0 border-t border-border/70 p-3 dark:border-white/5", variant === "full" && "px-6 pb-5")}>
        <div className={cn("mx-auto flex items-end gap-2 rounded-2xl bg-card p-1.5 ring-1 ring-black/[0.06] focus-within:ring-primary/40 dark:ring-white/10", variant === "full" && "max-w-3xl")}>
          <Textarea
            ref={inputRef}
            data-testid="ai-assistant-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!streaming) send();
              }
            }}
            rows={variant === "full" ? 2 : 1}
            placeholder={streaming ? "Svarer …" : variant === "full" ? "Spør, eller be om noe: «Reserver 12 B24 til Mongstad fra 1. des»" : "Spør, eller be om noe …"}
            className="min-h-[38px] max-h-40 flex-1 resize-none border-0 bg-transparent px-2.5 py-2 text-[13px] shadow-none focus-visible:ring-0"
          />
          {streaming ? (
            <button type="button" onClick={stop} className="mb-0.5 inline-flex h-8 w-8 items-center justify-center rounded-xl bg-foreground text-background transition-colors hover:bg-foreground/85" aria-label="Stopp" data-testid="ai-assistant-stop-button">
              <Square className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button type="button" onClick={() => send()} disabled={!draft.trim()} className="mb-0.5 inline-flex h-8 w-8 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40" aria-label="Send" data-testid="ai-assistant-send-button">
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className={cn("mx-auto mt-1.5 flex items-center justify-between px-1 text-[10.5px] text-muted-foreground", variant === "full" && "max-w-3xl")}>
          <span>Svar bygger kun på data i systemet · endringer krever bekreftelse</span>
          <span className="hidden sm:inline">⌘J åpner/lukker</span>
        </div>
      </div>
    </div>
  );
};
